import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Dropdown,
  Empty,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography
} from "antd";
import { CalculatorOutlined, DatabaseOutlined, FileTextOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import {
  importBasket,
  listBaskets,
  listTextBlocks,
  loadMeta,
  offerItems,
  searchArticles
} from "./api";
import {
  adjustCalc,
  normalizeSettings,
  summarizeMargin,
  unitPriceOf,
  lineNet,
  itemUnitPrice
} from "./calc";
import {
  KIND_COLORS,
  bpToPercent,
  decimalInputProps,
  formatEuro,
  formatMillicents,
  formatPercent,
  kindLabels,
  percentToBp
} from "./format";
import "./materials.css";

// Offer-editor tools of the material catalog, hooked into commons/Billing/Offers/AddOffers:
//   <OfferCatalogButton onAdd={items => …} />   search dialog → calculated offer items
//   <OfferTextField kind="intro" … />            intro / closing text with text blocks
//   <OfferLineExtras item onChange />            long text and the line's hidden calculation
//   <OfferMargin items />                        Deckungsbeitrag (owners only)
// Everything renders nothing for users without access to the catalog.

const useCatalogMeta = () => {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let alive = true;
    loadMeta()
      .then(m => alive && setMeta(m))
      .catch(() => alive && setMeta(null));
    return () => {
      alive = false;
    };
  }, []);
  return meta;
};

/** Text blocks of one kind as a dropdown; picking one calls onPick(text). */
const TextBlockMenu = ({ kind, onPick, label }) => {
  const { t } = useTranslation("dashboard");
  const [blocks, setBlocks] = useState(null);
  const load = () =>
    blocks ||
    listTextBlocks()
      .then(res => setBlocks(res.data.data.filter(b => b.kind === kind || b.kind === "other")))
      .catch(generalErrorAlert);
  return (
    <Dropdown
      trigger={["click"]}
      onOpenChange={open => open && load()}
      menu={{
        items: blocks?.length
          ? blocks.map(b => ({ key: b._id, label: b.title }))
          : [
              {
                key: "none",
                label: blocks ? t("No text blocks yet") : t("Loading..."),
                disabled: true
              }
            ],
        onClick: ({ key }) => {
          const block = (blocks || []).find(b => b._id === key);
          if (block) onPick(block.text);
        }
      }}
    >
      <Button size="small" icon={<FileTextOutlined />}>
        {label || t("Insert text block")}
      </Button>
    </Dropdown>
  );
};

const appendText = (current, text) => (current ? `${current}\n${text}` : text);

/** Intro or closing text of the offer, with text blocks. */
export const OfferTextField = ({ kind, value, onChange }) => {
  const { t } = useTranslation("dashboard");
  const meta = useCatalogMeta();
  return (
    <div className="materials-tools materials-tools--block mat-section">
      <Space wrap className="mat-full">
        <Typography.Text strong>
          {kind === "intro" ? t("Intro text") : t("Closing text")}
        </Typography.Text>
        {meta ? (
          <TextBlockMenu kind={kind} onPick={text => onChange(appendText(value, text))} />
        ) : null}
      </Space>
      <Input.TextArea
        autoSize={{ minRows: 2, maxRows: 8 }}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={
          kind === "intro" ? t("Printed above the positions") : t("Printed below the totals")
        }
      />
    </div>
  );
};

/** Search dialog: pick articles with quantities, or take a wholesaler basket. */
const CatalogPicker = ({ open, onClose, onAdd }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const numbers = decimalInputProps(lng);
  const kinds = kindLabels(t);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState({});
  const [baskets, setBaskets] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchArticles({ q, page, pageSize: 15 });
        setResult(res.data);
      } catch (error) {
        generalErrorAlert(error);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, q, page]);

  useEffect(() => {
    if (!open) {
      setPicked({});
      setBaskets(null);
    }
  }, [open]);

  const lines = Object.values(picked).filter(p => p.quantity > 0);
  const add = async () => {
    setBusy(true);
    try {
      const res = await offerItems(
        lines.map(p => ({ article: p.article._id, quantity: p.quantity }))
      );
      onAdd(res.data.data);
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };
  const takeBasket = async basket => {
    setBusy(true);
    try {
      const res = await importBasket(basket._id, { target: "items" });
      onAdd(res.data.data.items);
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (article, quantity) =>
    setPicked(prev => {
      const next = { ...prev };
      if (quantity > 0) next[article._id] = { article, quantity };
      else delete next[article._id];
      return next;
    });

  const searchTab = (
    <div className="materials-tools materials-tools--block">
      <Input.Search
        allowClear
        autoFocus
        placeholder={t("Search by name, article number or EAN")}
        value={q}
        onChange={e => {
          setQ(e.target.value);
          setPage(1);
        }}
      />
      <Spin spinning={loading}>
        {result.data.length ? (
          result.data.map(a => (
            <div className="mat-pick-row" key={a._id}>
              <div className="mat-pick-row__text mat-wrap">
                <div>
                  <Tag color={KIND_COLORS[a.kind]}>{kinds[a.kind]}</Tag>
                  {a.name}
                </div>
                <div className="mat-muted">
                  {[a.articleNumber, a.supplierName, a.shortText2].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="mat-pick-row__qty">
                <span className="mat-number">
                  {formatEuro(a.unitPrice, lng)} / {a.unit}
                </span>
                <InputNumber
                  {...numbers}
                  min={0}
                  placeholder={t("Qty")}
                  value={picked[a._id]?.quantity}
                  onChange={value => toggle(a, Number(value || 0))}
                  inputMode="decimal"
                />
              </div>
            </div>
          ))
        ) : (
          <Empty description={loading ? t("Loading...") : t("No articles found")} />
        )}
      </Spin>
      {result.total > 15 ? (
        <Pagination
          simple
          current={page}
          pageSize={15}
          total={result.total}
          onChange={setPage}
          className="mat-section"
        />
      ) : null}
    </div>
  );

  const basketTab = (
    <div className="materials-tools materials-tools--block">
      {baskets === null ? (
        <Spin />
      ) : baskets.length ? (
        baskets.map(b => (
          <div className="mat-pick-row" key={b._id}>
            <div className="mat-pick-row__text mat-wrap">
              <div>{b.supplier?.name}</div>
              <div className="mat-muted">
                {t("{{count}} positions", { count: b.basket?.items?.length || 0 })}
                {b.status === "imported" ? ` · ${t("Taken over")}` : ""}
              </div>
            </div>
            <Button loading={busy} onClick={() => takeBasket(b)}>
              {t("Add positions")}
            </Button>
          </div>
        ))
      ) : (
        <Empty description={t("No baskets received yet")} />
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      title={t("Add from catalog")}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="add" type="primary" disabled={!lines.length} loading={busy} onClick={add}>
          {t("Add {{count}} positions", { count: lines.length })}
        </Button>
      ]}
    >
      <Tabs
        items={[
          { key: "search", label: t("Catalog"), children: searchTab },
          { key: "baskets", label: t("Wholesaler baskets"), children: basketTab }
        ]}
        onChange={key =>
          key === "baskets" &&
          baskets === null &&
          listBaskets()
            .then(res => setBaskets(res.data.data))
            .catch(error => {
              setBaskets([]);
              generalErrorAlert(error);
            })
        }
      />
    </Modal>
  );
};

/** "Add from catalog" button for the offer editor. */
export const OfferCatalogButton = ({ onAdd }) => {
  const { t } = useTranslation("dashboard");
  const meta = useCatalogMeta();
  const [open, setOpen] = useState(false);
  if (!meta) return null;
  return (
    <>
      <Button
        size="large"
        block
        icon={<DatabaseOutlined />}
        onClick={() => setOpen(true)}
        className="mat-section"
      >
        {t("Add from catalog")}
      </Button>
      <CatalogPicker open={open} onClose={() => setOpen(false)} onAdd={onAdd} />
    </>
  );
};

/**
 * Per offer line: its long text (with position text blocks) and, for catalog lines, the
 * calculation behind the price (markup, minutes, wage group). Changing the calculation sets the
 * price; a price typed by hand stays and is marked as such.
 */
export const OfferLineExtras = ({ item, onChange }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const numbers = decimalInputProps(lng);
  const meta = useCatalogMeta();
  const [textOpen, setTextOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const settings = useMemo(() => (meta ? normalizeSettings(meta.settings) : null), [meta]);
  const calc = item?.calc;
  const calculated = calc ? unitPriceOf(calc) : null;
  const manual = calc && calculated !== itemUnitPrice(item);

  const applyCalc = next => {
    const price = unitPriceOf(next) / 100;
    onChange({
      ...item,
      calc: next,
      laborMinutes: next.minutes,
      price,
      total: lineNet(item.quantity, unitPriceOf(next)) / 100
    });
  };

  return (
    <Space wrap size={4} className="materials-tools">
      <Button
        size="small"
        icon={<FileTextOutlined />}
        onClick={() => {
          setDraft(item.longText || "");
          setTextOpen(true);
        }}
      >
        {item.longText ? t("Long text ✓") : t("Long text")}
      </Button>
      {calc && settings ? (
        <Button size="small" icon={<CalculatorOutlined />} onClick={() => setCalcOpen(true)}>
          {t("Calculation")}
          {manual ? ` · ${t("price edited")}` : ""}
        </Button>
      ) : null}
      <Modal
        open={textOpen}
        title={t("Long text")}
        onCancel={() => setTextOpen(false)}
        onOk={() => {
          onChange({ ...item, longText: draft });
          setTextOpen(false);
        }}
        okText={t("Apply")}
        cancelText={t("Cancel")}
        destroyOnClose
      >
        <div className="materials-tools materials-tools--block">
          {meta ? (
            <TextBlockMenu
              kind="position"
              onPick={text => setDraft(prev => appendText(prev, text))}
            />
          ) : null}
          <Input.TextArea
            className="mat-section"
            autoSize={{ minRows: 5, maxRows: 14 }}
            value={draft}
            onChange={e => setDraft(e.target.value)}
          />
        </div>
      </Modal>
      {calc && settings ? (
        <Modal
          open={calcOpen}
          title={t("Calculation")}
          onCancel={() => setCalcOpen(false)}
          footer={[
            manual ? (
              <Button key="reset" onClick={() => applyCalc(calc)}>
                {t("Use calculated price")}
              </Button>
            ) : null,
            <Button key="close" type="primary" onClick={() => setCalcOpen(false)}>
              {t("Close")}
            </Button>
          ]}
        >
          <div className="materials-tools materials-tools--block">
            <div className="mat-calc-grid">
              <span>{t("Material cost per unit")}</span>
              <span className="mat-number">{formatMillicents(calc.materialCost, lng)}</span>
              <span>{t("Material markup")}</span>
              <InputNumber
                {...numbers}
                addonAfter="%"
                min={-100}
                max={1000}
                value={bpToPercent(calc.markup)}
                onChange={value =>
                  applyCalc(adjustCalc(calc, { markup: percentToBp(value) }, settings))
                }
              />
              <span>{t("Minutes per unit")}</span>
              <InputNumber
                {...numbers}
                min={0}
                value={calc.minutes}
                onChange={value => applyCalc(adjustCalc(calc, { minutes: value || 0 }, settings))}
              />
              <span>{t("Wage group")}</span>
              <Select
                value={calc.wageGroup}
                onChange={value => applyCalc(adjustCalc(calc, { wageGroup: value }, settings))}
                options={settings.wageGroups.map(g => ({
                  value: g.code,
                  label: `${g.code} – ${g.name || g.code}`
                }))}
              />
              <span>{t("Hourly rate")}</span>
              <span className="mat-number">{formatEuro(calc.rate, lng)}</span>
              <span>{t("Calculated unit price")}</span>
              <strong className="mat-number">{formatEuro(calculated, lng)}</strong>
            </div>
            {manual ? (
              <Alert
                className="mat-section"
                type="info"
                showIcon
                message={t("The price was changed by hand: {{price}}", {
                  price: formatEuro(itemUnitPrice(item), lng)
                })}
              />
            ) : null}
            {calc.fixedPrice ? (
              <Typography.Paragraph className="mat-muted mat-section">
                {t("This article has a fixed price.")}
              </Typography.Paragraph>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </Space>
  );
};

/** Deckungsbeitrag of the offer while editing (company owner only). */
export const OfferMargin = ({ items }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const meta = useCatalogMeta();
  const margin = useMemo(() => summarizeMargin(items), [items]);
  if (!meta?.permissions?.margin || !margin.calculatedLines) return null;
  return (
    <div className="materials-tools materials-tools--block mat-margin">
      <Descriptions
        size="small"
        bordered
        column={{ xs: 1, md: 3 }}
        title={t("Contribution margin (internal)")}
      >
        <Descriptions.Item label={t("Revenue")}>
          {formatEuro(margin.revenue, lng)}
        </Descriptions.Item>
        <Descriptions.Item label={t("Material cost")}>
          {formatEuro(margin.materialCost, lng)}
        </Descriptions.Item>
        <Descriptions.Item label={t("Wage cost")}>
          {formatEuro(margin.labourCost, lng)} ({Math.round(margin.labourMinutes / 6) / 10} h)
        </Descriptions.Item>
        <Descriptions.Item label={t("Contribution margin")}>
          <strong>{formatEuro(margin.contribution, lng)}</strong> (
          {formatPercent(margin.margin, lng)})
        </Descriptions.Item>
      </Descriptions>
      {margin.uncalculatedRevenue ? (
        <Typography.Paragraph className="mat-muted mat-section">
          {t("{{amount}} come from positions without a calculation; their cost is not included.", {
            amount: formatEuro(margin.uncalculatedRevenue, lng)
          })}
        </Typography.Paragraph>
      ) : null}
    </div>
  );
};
